/**
 * Builds TableSection for field mapping (source field + target attribute) for AddFormComponent.
 */
import type { TableSection } from '@shared/components/add-form/add-form.component';
import type { TableColumn } from '@shared/components/data-table/data-table.component';
import type { ProductFieldMappingRow, SkuProductFieldOption } from './product-field-mapping.types';
import { getProductFieldMappingTableData, getOptionalFieldsForDropdown } from './product-field-mapping.util';

export const PRODUCT_FIELD_MAPPING_SECTION_NAME = 'product_field_mapping';
export const ORDER_FIELD_MAPPING_SECTION_NAME = 'order_field_mapping';

export const DEFAULT_FIELD_MAPPING_I18N = {
  title: 'onlineStore.form.product_field_mapping.title',
  columnSkuField: 'onlineStore.form.product_field_mapping.table.columns.sku_field',
  columnTargetAttribute: 'onlineStore.form.product_field_mapping.table.columns.magento_field',
  columnSyncDirection: 'onlineStore.form.product_field_mapping.table.columns.sync_direction',
  columnDataType: 'onlineStore.form.product_field_mapping.table.columns.data_type',
  columnActions: 'onlineStore.form.product_field_mapping.table.columns.actions',
  addPlaceholder: 'onlineStore.form.product_field_mapping.actions.add_field',
  placeholderTemplate: 'onlineStore.form.product_field_mapping.placeholders.enter_label_for_key',
} as const;

export interface BuildProductFieldMappingSectionOptions {
  mappings: ProductFieldMappingRow[];
  fieldsConfig: SkuProductFieldOption[];
  requiredFieldCodes: string[];
  translate: (key: string, params?: Record<string, string>) => string;
  viewMode: boolean;
  insertAfterSection: string;
  onAddRow: (skuFieldCode: string) => void;
  onAddDropdownMenuHide?: () => void;
  i18n?: Partial<Record<keyof typeof DEFAULT_FIELD_MAPPING_I18N, string>>;
  sectionName?: string;
  /** Options for Thuộc tính Magento column (from API v1/online/shop/{shopId}/magento/product/attributes). When set, column is select; otherwise input. */
  magentoAttributeOptions?: { label: string; value: string }[];
}

/** Parent must handle tableAction: cellValueChange (magentoAttribute) and delete (optional rows only). */
export function buildProductFieldMappingTableSection(
  options: BuildProductFieldMappingSectionOptions
): TableSection {
  const {
    mappings,
    fieldsConfig,
    requiredFieldCodes,
    translate,
    viewMode,
    insertAfterSection,
    onAddRow,
    onAddDropdownMenuHide,
    i18n = {},
    sectionName = PRODUCT_FIELD_MAPPING_SECTION_NAME,
    magentoAttributeOptions,
  } = options;
  const t = { ...DEFAULT_FIELD_MAPPING_I18N, ...i18n };

  const SYNC_DIRECTION_OPTIONS: { label: string; value: string }[] = [
    {
      label: 'onlineStore.form.product_field_mapping.sync_direction.inbound',
      value: 'INBOUND',
    },
    {
      label: 'onlineStore.form.product_field_mapping.sync_direction.outbound',
      value: 'OUTBOUND',
    },
    {
      label: 'onlineStore.form.product_field_mapping.sync_direction.bidirectional',
      value: 'BIDIRECTIONAL',
    },
  ];

  const DATA_TYPE_OPTIONS: { label: string; value: string }[] = [
    { label: 'String', value: 'STRING' },
    { label: 'Decimal', value: 'DECIMAL' },
    { label: 'Boolean', value: 'BOOLEAN' },
    { label: 'Integer', value: 'INTEGER' },
    { label: 'Datetime', value: 'DATETIME' },
  ];

  const magentoAttrColumn: TableColumn = {
    label: t.columnTargetAttribute,
    prop: 'magentoAttribute',
    width: 280,
    align: 'left',
    headerAlign: 'center',
    disabled: viewMode,
    ...(magentoAttributeOptions !== undefined
      ? {
          type: 'select' as const,
          options: magentoAttributeOptions,
          placeholder: t.placeholderTemplate ? translate(t.placeholderTemplate, { keyLabel: '' }) : undefined,
          filter: true,
        }
      : {
          type: 'inputText' as const,
          placeholderProp: 'magentoAttributePlaceholder',
        }),
  };

  const columns: TableColumn[] = [
    {
      label: t.columnSkuField,
      prop: 'skuFieldCodeLabel',
      width: 240,
      align: 'left',
      headerAlign: 'center',
    },
    magentoAttrColumn,
    {
      label: t.columnSyncDirection,
      prop: 'syncDirection',
      width: 180,
      align: 'left',
      headerAlign: 'center',
      type: 'select',
      options: SYNC_DIRECTION_OPTIONS,
      placeholder:
        'onlineStore.form.product_field_mapping.sync_direction.placeholder',
      disabled: viewMode,
    },
    {
      label: t.columnDataType,
      prop: 'dataType',
      width: 140,
      align: 'left',
      headerAlign: 'center',
      type: 'select',
      options: DATA_TYPE_OPTIONS,
      placeholder:
        'onlineStore.form.product_field_mapping.data_type.placeholder',
      disabled: viewMode,
    },
    {
      label: t.columnActions,
      prop: 'actions',
      width: 100,
      align: 'center',
      headerAlign: 'center',
      type: 'actions',
    },
  ];

  const data = getProductFieldMappingTableData(
    mappings,
    fieldsConfig,
    requiredFieldCodes,
    translate,
    t.placeholderTemplate
  );
  const addDropdownOptions = getOptionalFieldsForDropdown(mappings, fieldsConfig);

  return {
    title: t.title,
    name: sectionName,
    show: true,
    insertAfterSection,
    columns,
    data,
    loading: false,
    useAdvancedTable: false,
    defaultColumns: columns,
    flexibleColumns: [],
    showPaginator: false,
    rows: data.length,
    first: 0,
    allowRowActions: !viewMode,
    addDropdownOptions,
    addDropdownPlaceholder: t.addPlaceholder,
    onAddDropdownSelect: onAddRow,
    onAddDropdownMenuHide,
  };
}
